type SocialPlatform = {
    hosts: string[];
    getHandle?: (segments: string[], url: URL) => string | null;
};

const SOCIAL_PLATFORM_CONFIG: SocialPlatform[] = [
    {
        hosts: ['instagram.com'],
    },
    {
        hosts: ['twitter.com', 'x.com'],
    },
    {
        hosts: ['facebook.com'],
    },
    {
        hosts: ['linkedin.com'],
        getHandle: (segments: string[]) => {
            if (!segments.length) {
                return null;
            }

            if (['in', 'company', 'school'].includes(segments[0]) && segments[1]) {
                return segments[1];
            }

            return segments[segments.length - 1];
        },
    },
    {
        hosts: ['github.com', 'gitlab.com'],
        getHandle: (segments: string[]) => segments[0] || null,
    },
    {
        hosts: ['tiktok.com'],
    },
    {
        hosts: ['youtube.com', 'youtu.be'],
        getHandle: (segments: string[], url: URL) => {
            if (segments.length === 0) {
                const abChannel = url.searchParams.get('ab_channel');
                return abChannel || null;
            }

            if (segments[0].startsWith('@')) {
                return segments[0].slice(1);
            }

            if (['channel', 'c', 'user'].includes(segments[0]) && segments[1]) {
                return segments[1];
            }

            return segments[segments.length - 1];
        },
    },
    {
        hosts: ['pinterest.com', 'snapchat.com', 'behance.net', 'dribbble.com', 'medium.com'],
    },
    {
        hosts: ['reddit.com'],
        getHandle: (segments: string[]) => {
            if (!segments.length) return null;
            if (['user', 'u'].includes(segments[0]) && segments[1]) {
                return segments[1];
            }

            return segments[segments.length - 1];
        },
    },
    {
        hosts: ['t.me', 'telegram.me'],
    },
];

const ensureProtocol = (value: string): string => {
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(value)) {
        return value;
    }

    return `https://${value}`;
};

const stripProtocol = (value: string): string =>
    value.replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//, '').replace(/^www\./i, '');

const cleanSegment = (segment: string): string => segment.replace(/^@/, '').trim();

const findSocialHandle = (url: URL): string | null => {
    const hostname = url.hostname.replace(/^www\./i, '').toLowerCase();
    const segments = url.pathname.split('/').filter((segment) => !!segment);

    const matcher = SOCIAL_PLATFORM_CONFIG.find((platform) =>
        platform.hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`))
    );

    if (!matcher) {
        return null;
    }

    const handleGetter = matcher.getHandle || ((items: string[]) => (items.length ? items[items.length - 1] : null));
    const rawHandle = handleGetter(segments, url);

    if (!rawHandle) {
        return null;
    }

    return cleanSegment(rawHandle);
};

export const formatLinkLabel = (value: string): string => {
    if (!value) {
        return '';
    }

    const trimmedValue = value.trim();

    try {
        const parsedUrl = new URL(ensureProtocol(trimmedValue));
        const handle = findSocialHandle(parsedUrl);

        if (handle) {
            return `@${handle}`;
        }

        const sanitized = stripProtocol(parsedUrl.href);
        return sanitized.replace(/\/$/, '');
    } catch (error) {
        return stripProtocol(trimmedValue).replace(/\/$/, '');
    }
};

export default formatLinkLabel;
